import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import { Play as PlayIcon, Plus as PlusIcon } from 'lucide-react';
import { useRoutineStore } from '../store/routineStore';
import { useWorkoutStore } from '../store/workoutStore';
import { useProgressStore } from '../store/progressStore';
import { RoutineCard, StatCard } from '../components/Cards';
import WeeklyPlan from '../components/WeeklyPlan';

const Dashboard = ({ onStartWorkout }) => {
  const routines = useRoutineStore((state) => state.routines);
  const workoutHistory = useWorkoutStore((state) => state.history);
  const streak = useProgressStore((state) => state.workoutStreak);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedRoutine, setSelectedRoutine] = useState(null);
  const [routineName, setRoutineName] = useState('');
  const createRoutine = useRoutineStore((state) => state.createRoutine);

  const handleCreateRoutine = () => {
    if (routineName.trim()) {
      createRoutine({
        name: routineName,
        days: [
          {
            id: 'day1',
            name: 'Día 1',
            exercises: [],
          },
        ],
      });
      setRoutineName('');
      setCreateDialogOpen(false);
    }
  };

  const handleSelectRoutineDay = (routine, day) => {
    setSelectedRoutine({ routine, day });
  };

  const handleConfirmWorkout = () => {
    if (selectedRoutine) {
      onStartWorkout(selectedRoutine.routine.id, selectedRoutine.day.id);
      setSelectedRoutine(null);
    }
  };

  const recentWorkouts = workoutHistory.slice(0, 3);

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
          ¡Bienvenido!
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
          {new Date().toLocaleDateString('es-ES', {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
          })}
        </Typography>
      </Box>

      {/* Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={4}>
          <StatCard
            icon="💪"
            label="Entrenamientos"
            value={workoutHistory.length}
            variant="success"
          />
        </Grid>
        <Grid item xs={6} sm={4}>
          <StatCard
            icon="🔥"
            label="Streak"
            value={`${streak}d`}
            variant="warning"
          />
        </Grid>
        <Grid item xs={6} sm={4}>
          <StatCard
            icon="🎯"
            label="Rutinas"
            value={routines.length}
            variant="info"
          />
        </Grid>
      </Grid>

      <WeeklyPlan onStartWorkout={onStartWorkout} />

      {/* Quick Start */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Mis Rutinas
          </Typography>
          <Button
            size="small"
            startIcon={<PlusIcon size={18} />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Nueva
          </Button>
        </Box>

        {routines.length === 0 ? (
          <Box
            sx={{
              p: 3,
              textAlign: 'center',
              bgcolor: 'grey.50',
              borderRadius: 2,
              border: '2px dashed',
              borderColor: 'grey.300',
            }}
          >
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
              Sin rutinas aún
            </Typography>
            <Button
              variant="contained"
              size="small"
              onClick={() => setCreateDialogOpen(true)}
            >
              Crear Primera Rutina
            </Button>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {routines.map((routine) => (
              <Grid item xs={12} sm={6} key={routine.id}>
                <RoutineCard
                  routine={routine}
                  onStart={() => handleSelectRoutineDay(routine, routine.days[0])}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      {/* Recent Workouts */}
      {recentWorkouts.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
            Entrenamientos Recientes
          </Typography>
          <List>
            {recentWorkouts.map((workout) => (
              <ListItem key={workout.id} disablePadding sx={{ mb: 1 }}>
                <Box
                  sx={{
                    width: '100%',
                    p: 1.5,
                    bgcolor: 'grey.50',
                    borderRadius: 1,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      {new Date(workout.date).toLocaleDateString()}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {workout.exercises.length} ejercicios • {workout.duration} min
                    </Typography>
                  </Box>
                  <Typography variant="caption" sx={{ bgcolor: 'success.light', px: 1.5, py: 0.5, borderRadius: 1 }}>
                    ✓ Completado
                  </Typography>
                </Box>
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      {/* Dialogs */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)}>
        <DialogTitle>Nueva Rutina</DialogTitle>
        <DialogContent sx={{ minWidth: 300, pt: 2 }}>
          <TextField
            autoFocus
            fullWidth
            label="Nombre de la rutina"
            value={routineName}
            onChange={(e) => setRoutineName(e.target.value)}
            placeholder="Ej: Push/Pull, PPL, Upper/Lower"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancelar</Button>
          <Button
            onClick={handleCreateRoutine}
            variant="contained"
            disabled={!routineName.trim()}
          >
            Crear
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!selectedRoutine}
        onClose={() => setSelectedRoutine(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Selecciona el día</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <List>
            {selectedRoutine?.routine.days.map((day) => (
              <ListItemButton
                key={day.id}
                onClick={() => handleSelectRoutineDay(selectedRoutine.routine, day)}
                sx={{
                  mb: 1,
                  bgcolor: 'grey.50',
                  borderRadius: 1,
                  '&:hover': { bgcolor: 'grey.100' },
                }}
              >
                <ListItemText
                  primary={day.name}
                  secondary={`${day.exercises.length} ejercicios`}
                />
              </ListItemButton>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedRoutine(null)}>Cancelar</Button>
          <Button
            onClick={handleConfirmWorkout}
            variant="contained"
            startIcon={<PlayIcon size={18} />}
          >
            Entrenar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Dashboard;
